/**
 * InvitePage — приём приглашения, установка пароля.
 * Маршрут: /cabinet/invite/:token
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Stack, Text, Title, PasswordInput,
  Button, Loader, Center, Alert, Checkbox,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconLock } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { invitesService } from '../services/api';
import { useParentSession } from '../context/ParentSessionContext';
import tokens from '../theme/designTokens';

const InvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useParentSession();

  const [checking, setChecking] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    let mounted = true;
    invitesService.check(token)
      .then((data) => {
        if (!mounted) return;
        if (data.valid) {
          setInviteData(data);
        } else {
          setError(data.error || 'Приглашение недействительно');
        }
      })
      .catch((e) => mounted && setError(e.message))
      .finally(() => mounted && setChecking(false));
    return () => { mounted = false; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirm) {
      notifications.show({
        title: 'Ошибка',
        message: 'Пароли не совпадают',
        color: 'red',
      });
      return;
    }
    if (password.length < 8) {
      notifications.show({
        title: 'Ошибка',
        message: 'Пароль должен быть не менее 8 символов',
        color: 'red',
      });
      return;
    }
    setSubmitting(true);
    try {
      await invitesService.accept(token, password, true);
      await refresh();
      notifications.show({
        title: 'Готово',
        message: 'Добро пожаловать в личный кабинет!',
        color: 'green',
        icon: <IconCheck size={18} />,
      });
      navigate('/');
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box style={{ minHeight: '100vh', background: tokens.colors.bg.page, paddingTop: 60 }}>
      <Container size="xs">
        {/* Логотип */}
        <Center mb="xl">
          <Box style={{ textAlign: 'center' }}>
            <Box
              style={{
                width: 80,
                height: 80,
                borderRadius: 24,
                background: tokens.gradients.hero,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontFamily: tokens.typography.fontFamilyHeading,
                fontWeight: 1000,
                fontSize: 32,
                boxShadow: '0 12px 32px rgba(124,58,237,0.3)',
                marginBottom: 16,
              }}
            >
              ЭИ
            </Box>
            <Title
              order={2}
              style={{
                fontFamily: tokens.typography.fontFamilyHeading,
                fontWeight: 1000,
                color: tokens.colors.text.primary,
              }}
            >
              Это <span style={{ color: tokens.colors.brand.orange }}>Интересно!</span>
            </Title>
            <Text c={tokens.colors.text.muted} fw={700} mt={4}>
              Личный кабинет родителя
            </Text>
          </Box>
        </Center>

        <Paper p="xl" radius="xl" shadow="md" style={{ background: 'white' }}>
          {checking ? (
            <Center py="xl">
              <Loader color="brand-purple" />
            </Center>
          ) : error ? (
            <Stack gap="md">
              <Alert
                icon={<IconAlertCircle size={20} />}
                color="red"
                radius="lg"
                title="Приглашение недействительно"
              >
                {error}
              </Alert>
              <Text size="sm" c={tokens.colors.text.muted} fw={700} ta="center">
                Если у вас есть учётная запись, войдите по логину и паролю.
              </Text>
              <Button
                fullWidth
                size="lg"
                color="brand-purple"
                onClick={() => navigate('/login')}
              >
                Войти
              </Button>
            </Stack>
          ) : (
            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                <Box>
                  <Title order={3} mb={4} style={{ color: tokens.colors.text.primary }}>
                    Здравствуйте{inviteData?.parent_name ? `, ${inviteData.parent_name}` : ''}!
                  </Title>
                  <Text size="sm" c={tokens.colors.text.muted} fw={700}>
                    Придумайте пароль, чтобы войти в свой личный кабинет
                  </Text>
                </Box>

                {inviteData?.parent_email && (
                  <Box
                    p="sm"
                    style={{
                      background: tokens.colors.bg.softPurple,
                      borderRadius: 14,
                    }}
                  >
                    <Text size="xs" c={tokens.colors.text.muted} fw={700}>
                      Email
                    </Text>
                    <Text size="md" fw={900} style={{ color: tokens.colors.text.primary }}>
                      {inviteData.parent_email}
                    </Text>
                  </Box>
                )}

                <PasswordInput
                  label="Пароль"
                  description="Минимум 8 символов, должны быть буквы и цифры"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  leftSection={<IconLock size={16} />}
                  required
                  size="md"
                />

                <PasswordInput
                  label="Повторите пароль"
                  placeholder="••••••••"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  leftSection={<IconLock size={16} />}
                  required
                  size="md"
                />

                <Checkbox
                  checked={consent}
                  onChange={(e) => setConsent(e.currentTarget.checked)}
                  radius="xl"
                  size="sm"
                  label={
                    <Text size="xs" c={tokens.colors.text.muted}>
                      Нажимая кнопку &laquo;Войти в кабинет&raquo;, я даю согласие на обработку
                      персональных данных согласно Политике конфиденциальности компании
                    </Text>
                  }
                />

                <Button
                  type="submit"
                  fullWidth
                  size="lg"
                  loading={submitting}
                  disabled={!consent}
                  color="brand-orange"
                  mt="sm"
                >
                  Войти в кабинет
                </Button>
              </Stack>
            </form>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default InvitePage;
